import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 8 algebra.
 *
 * The shift these lessons carry is from *doing* to *having*: a
 * relationship stops being a calculation you perform and becomes an object you
 * can name, graph, compare and solve with. That is what makes Algebra I
 * possible, and it is why `functions-intro` comes before `linear-functions`
 * rather than after it.
 */
export const FUNCTION_LESSONS: Lesson[] = [
  lesson({
    id: 'functions-intro-01',
    unitId: 'unit-functions',
    conceptId: 'functions-intro',
    title: 'Functions',
    summary: 'A rule that gives exactly one output for each input — a relationship you can hold rather than perform.',
    objectives: [
      'Decide whether a relation is a function',
      'Evaluate a function written in f(x) notation',
      'Read a function from a table, a graph or a rule',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A machine with one rule',
        blocks: [
          text('Put a number in; get a number out. A vending machine takes a code and returns an item; a rule takes 3 and returns 11.'),
          math('f(x) = 3x + 2 \\qquad f(3) = 11'),
          text('A **function** is any such rule with one requirement attached: **each input gives exactly one output**. Press the same button twice and you must get the same thing both times, or it is not a machine anybody could use.'),
        ],
      },
      intuition: {
        title: 'The one rule, and the notation',
        blocks: [
          text('That requirement is the whole definition, and it is one-directional in a way worth being careful about.'),
          insight('Two different inputs may share an output — that is fine. What is forbidden is one input having two different outputs. A vending machine that sometimes gives crisps and sometimes chocolate for the same code is broken; one that gives crisps for two different codes is merely generous.'),
          table(['Pairs', 'Function?', 'Why'], [
            ['(1, 5), (2, 7), (3, 9)', 'Yes', 'every input appears once'],
            ['(1, 5), (2, 5), (3, 5)', 'Yes', 'outputs may repeat'],
            ['(1, 5), (1, 7), (2, 9)', 'No', 'input 1 gives two outputs'],
            ['(4, 2), (4, −2)', 'No', 'input 4 gives two outputs'],
          ]),
          text('On a graph this has a neat consequence. If any vertical line crosses the graph twice, that x has two ys — so it is not a function. That is the **vertical line test**, and it is the definition drawn rather than a separate fact.'),
          text('Now the notation, which trips people up more than the idea does.'),
          math('f(x) \\ne f \\times x'),
          warning('f(x) is read "f of x" and means the output of the rule f when the input is x. The brackets are not multiplication — f(3) means "put 3 in", not "f times 3".', 'Not a multiplication'),
          insight('The notation earns its keep by letting you talk about the rule itself. f(3) is a number; f is a *thing* — something you can graph, compare with g, and later differentiate. That shift from calculation to object is what Grade 8 is really for.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 8,
              points: [
                { x: -1, y: -1, label: 'f(−1)' },
                { x: 0, y: 2 },
                { x: 1, y: 5 },
                { x: 2, y: 8, label: 'f(2)' },
              ],
            },
            'Each input has exactly one point above or below it. No vertical line meets this set twice.',
          ),
          text('The same function can be shown four ways — as a rule, a table, a graph, or a description in words — and being able to move between them is most of the skill:'),
          table(['Form', 'This function'], [
            ['Rule', 'f(x) = 3x + 2'],
            ['Table', '(0, 2), (1, 5), (2, 8)'],
            ['Graph', 'a straight line rising 3 for each step right'],
            ['Words', 'triple the input, then add two'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For f(x) = 4x − 7, find f(3) and f(−2). Then decide whether (2, 5), (3, 9), (2, 6) is a function.'),
          steps([
            { label: 'Substitute 3 for x.', latex: 'f(3) = 4(3) - 7 = 5' },
            { label: 'Substitute −2, keeping the signs.', latex: 'f(-2) = 4(-2) - 7 = -15' },
            { label: 'For the pairs, look at the inputs.', latex: '2,\\ 3,\\ 2' },
            { label: 'The input 2 appears twice, with different outputs.', latex: '(2, 5) \\text{ and } (2, 6)' },
          ]),
          text('So it is not a function. Note that nothing about the *outputs* was wrong — 5 and 6 are perfectly reasonable numbers. It failed because one input could not decide.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('functions-intro', 4)] },
      independent: { title: 'On your own', blocks: [practice('functions-intro', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('functions-intro', 3, 'Deciding which relations qualify.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A function gives exactly one output for each input.',
            'Repeated outputs are fine; repeated inputs with different outputs are not.',
            'The vertical line test is that rule drawn on a graph.',
            'f(x) means "the output of f at x" — the brackets are not multiplication.',
            'A function can be given as a rule, a table, a graph or a sentence.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'linear-functions-01',
    unitId: 'unit-functions',
    conceptId: 'linear-functions',
    title: 'Linear Functions and Slope',
    summary: 'Straight-line relationships, and what slope measures: how fast one quantity changes with another.',
    objectives: [
      'Compute slope as rise over run',
      'Read the slope and intercept from y = mx + c',
      'Use a linear rule to predict a value',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Constant change',
        blocks: [
          text('A plumber charges £40 to come out, then £30 an hour. After one hour you owe £70; after two, £100; after three, £130.'),
          text('Every extra hour adds exactly £30. That constancy is what makes the relationship **linear**, and it is why the graph is a straight line rather than a curve.'),
          math('y = 30x + 40'),
        ],
      },
      intuition: {
        title: 'Slope is a rate',
        blocks: [
          text('The 30 and the 40 do different jobs, and telling them apart is the core skill of this lesson.'),
          list([
            '**40** is the value when x is 0 — the call-out fee, paid before any work. On the graph it is where the line crosses the y-axis: the **y-intercept**.',
            '**30** is the change in y for each step of 1 in x — the hourly rate. On the graph it is the **slope**.',
          ]),
          insight('Slope is rise over run: how far up for each step across. It is not a position on the graph, it is a *rate* — and that makes it the same idea as the unit rate from Grade 6 and the constant of proportionality from Grade 7, met a third time and finally named.'),
          math('m = \\frac{\\text{rise}}{\\text{run}} = \\frac{y_{2} - y_{1}}{x_{2} - x_{1}}'),
          warning('Rise over run, not run over rise. Inverting it gives a real quantity — how long it takes to climb one unit — which is not slope.', 'The right way up'),
          table(['Slope', 'The line', 'Meaning'], [
            ['positive', 'rises left to right', 'y grows as x grows'],
            ['negative', 'falls left to right', 'y shrinks as x grows'],
            ['zero', 'flat', 'y never changes'],
            ['undefined', 'vertical', 'run is zero — and not a function'],
          ]),
          text('A proportional relationship from Grade 7 is simply a linear one whose intercept is zero. That is why it passes through the origin: with no call-out fee, zero hours costs nothing.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -8,
              max: 8,
              lines: [
                { slope: 2, intercept: 1, label: 'm = 2' },
                { slope: -1, intercept: -2, label: 'm = −1', dashed: true },
              ],
            },
            'Two lines. The solid one climbs two for every one across; the dashed one falls one for every one across, and its intercept is below the origin.',
          ),
          tip('To sketch y = mx + c, mark the intercept on the y-axis, then step across 1 and up m, and join. Two points are enough for a line, and the intercept is free.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A line passes through (2, 3) and (6, 15). Find its slope and its equation, then predict y when x = 10.'),
          steps([
            { label: 'Find the rise.', latex: '15 - 3 = 12' },
            { label: 'Find the run.', latex: '6 - 2 = 4' },
            { label: 'Divide.', latex: 'm = \\frac{12}{4} = 3' },
            { label: 'Use one point to find the intercept.', latex: '3 = 3(2) + c \\;\\Rightarrow\\; c = -3' },
            { label: 'Write the equation.', latex: 'y = 3x - 3' },
            { label: 'Predict at x = 10.', latex: 'y = 3(10) - 3 = 27' },
          ]),
          text('Check it with the other point: 3(6) − 3 = 15. Both given points satisfy the equation, so the line is right.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-functions', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('linear-functions', 3, 'Negative slopes, and reading a rule out of a situation.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A linear relationship changes by the same amount every step, so its graph is straight.',
            'Slope is rise over run — a rate, not a position.',
            'In y = mx + c, m is the slope and c is the y-intercept.',
            'Negative slope falls; zero slope is flat; a vertical line has no slope and is not a function.',
            'A proportional relationship is a linear one with an intercept of zero.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'comparing-functions-01',
    unitId: 'unit-functions',
    conceptId: 'comparing-functions',
    title: 'Linear and Nonlinear Functions',
    summary: 'Telling a constant rate from a changing one, and comparing two relationships given in two different forms.',
    objectives: [
      'Decide whether a table, rule or graph is linear',
      'Find a rate of change from a table',
      'Compare two functions given in different forms',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two savers',
        blocks: [
          text('Ama saves £6 a week and started with £10. Ben started with £40 and his savings double every month. After a month Ben is far ahead. After a year he owns a small country.'),
          text('Both are functions of time. Only one of them is **linear**, and the difference is not how fast they grow — it is whether the growth ever changes.'),
        ],
      },
      intuition: {
        title: 'Equal steps in, equal steps out',
        blocks: [
          text('A function is linear when equal steps in x always produce equal steps in y. That single test settles every case, whichever form the function arrives in.'),
          table(['Given as', 'Linear when', 'Nonlinear when'], [
            ['a table', 'the y-differences are all the same', 'the differences change'],
            ['a rule', 'x appears alone, to the first power', 'x is squared, cubed, in a denominator, or an exponent'],
            ['a graph', 'the points lie on a straight line', 'it bends'],
            ['words', 'a fixed amount per unit', 'a percentage, a doubling, an area'],
          ]),
          warning('Rising steadily is not the same as rising linearly. y = x² rises the whole way and is not linear; its differences are 1, 3, 5, 7 — growing every step. Check the differences, not the direction.', 'Rising is not enough'),
          insight('A table only tells you the differences are equal if the x-values step evenly. If x goes 0, 1, 3, 4, a y-difference across the gap of 2 covers two steps, so halve it before comparing. Divide by the change in x every time and the trap disappears — that division is the rate of change, and it is the same slope from the last lesson.'),
          math('\\text{rate of change} = \\frac{\\text{change in } y}{\\text{change in } x}'),
          text('Once every function is reduced to a rate and a starting value, two of them can be compared even when they arrive in different clothes — one as an equation, one as a table, one as a sentence.'),
          warning('Compare the right thing. "Which grows faster" is a question about the *rate*; "which is ahead at the start" is a question about the y-intercept. A function can lose on one and win on the other, and given long enough the faster rate always wins.', 'Rate or head start?'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -2,
              max: 10,
              points: [
                { x: 0, y: 0 },
                { x: 1, y: 1 },
                { x: 2, y: 4 },
                { x: 3, y: 9 },
              ],
              lines: [{ slope: 3, intercept: 0, label: 'y = 3x', dashed: true }],
            },
            'The dashed line steps up 3 every time. The plotted points step up 1, then 3, then 5 — the same climb overall, arrived at unevenly. Only one of the two is linear.',
          ),
          table(['x', 'y = 3x', 'y = x²'], [
            ['0', '0', '0'],
            ['1', '3', '1'],
            ['2', '6', '4'],
            ['3', '9', '9'],
            ['4', '12', '16'],
          ]),
          tip('Two functions can agree at two points and disagree everywhere else — these agree at 0 and 3. Two matching values never establish that two functions are the same.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Function A is given by y = 4x + 5. Function B is the table (0, 12), (2, 18), (4, 24). Which has the greater rate of change, and which is ahead when x = 0?'),
          steps([
            { label: 'Read A’s rate straight off the equation.', latex: 'm_{A} = 4' },
            { label: 'For B, take a change in y and the change in x that produced it.', latex: '18 - 12 = 6, \\quad 2 - 0 = 2' },
            { label: 'Divide — x stepped by 2, not by 1.', latex: 'm_{B} = \\tfrac{6}{2} = 3' },
            { label: 'Compare the rates.', latex: '4 > 3' },
            { label: 'Compare the values at x = 0.', latex: '5 < 12' },
          ]),
          text('So B starts ahead but A climbs faster, and A overtakes it. Reporting B’s rate as 6 — the change in y with the division forgotten — would have reversed the whole answer.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('comparing-functions', 4)] },
      independent: { title: 'On your own', blocks: [practice('comparing-functions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('comparing-functions', 3, 'Tables whose x-values do not step by one, and functions given two different ways.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A function is linear when equal steps in x give equal steps in y.',
            'In a rule, x alone to the first power is linear; squared, cubed or as an exponent is not.',
            'Always divide the change in y by the change in x — a table need not step by one.',
            'y = mx + c is the general linear function; m is the rate and c the starting value.',
            'To compare two functions, reduce both to a rate and a starting value, whatever form they came in.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'systems-of-equations-01',
    unitId: 'unit-functions',
    conceptId: 'systems-of-equations',
    title: 'Systems of Equations',
    summary: 'Two conditions at once, and the single point that satisfies both.',
    objectives: [
      'Understand the solution of a system as the intersection of two lines',
      'Solve a system by substitution and by elimination',
      'Recognise systems with no solution or infinitely many',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Two conditions at once',
        blocks: [
          text('Eight tickets are bought, some at £5 and some at £8, for £52 in total. How many of each?'),
          text('Neither fact settles it alone. Together they do:'),
          math('\\begin{cases} x + y = 8 \\\\ 5x + 8y = 52 \\end{cases}'),
          text('A **system of equations** is two or more conditions that must hold *simultaneously*, and its solution is the pair of values satisfying every one of them.'),
        ],
      },
      intuition: {
        title: 'Where the lines cross',
        blocks: [
          text('Each equation, on its own, is satisfied by infinitely many pairs — a whole line of them. The solution to the system is the point lying on both lines at once, which is where they cross.'),
          insight('That picture explains every case at a glance. Two lines that cross meet at exactly one point, so there is one solution. Parallel lines never meet: no solution. Identical lines meet everywhere: infinitely many.'),
          text('There are two ways to find the point without drawing it.'),
          list([
            '**Substitution** — rearrange one equation for a single unknown and put that expression into the other. Best when one variable is already alone, or nearly so.',
            '**Elimination** — add or subtract the equations so that one variable cancels. Best when the coefficients already match or are easy to match.',
          ], true),
          warning('Whichever route you take, you are not finished when you have one number. A solution is a *pair*, and it must satisfy both equations — substituting back into only the equation you used will not catch a mistake made in it.', 'Check both'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -6,
              max: 10,
              lines: [
                { slope: -1, intercept: 8, label: 'x + y = 8' },
                { slope: -0.625, intercept: 6.5, label: '5x + 8y = 52', dashed: true },
              ],
              points: [{ x: 4, y: 4, label: '(4, 4)' }],
            },
            'The two conditions as lines. Only one point lies on both, and that point is the answer to the ticket question.',
          ),
          text('So four tickets at £5 and four at £8 — which is eight tickets costing £20 + £32 = £52, as required.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve the ticket system by elimination.'),
          steps([
            { label: 'Multiply the first equation by 5 so the x terms match.', latex: '5x + 5y = 40' },
            { label: 'Subtract it from the second.', latex: '(5x + 8y) - (5x + 5y) = 52 - 40' },
            { label: 'The x terms cancel.', latex: '3y = 12 \\;\\Rightarrow\\; y = 4' },
            { label: 'Substitute back into the simpler equation.', latex: 'x + 4 = 8 \\;\\Rightarrow\\; x = 4' },
            { label: 'Check in the *other* equation.', latex: '5(4) + 8(4) = 52 \\;\\checkmark' },
          ]),
          tip('The last step is the one people skip and the one that catches errors. Check in the equation you did *not* use to find the second value.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('systems-of-equations', 4)] },
      independent: { title: 'On your own', blocks: [practice('systems-of-equations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('systems-of-equations', 3, 'Systems where neither variable cancels without work.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A system is several conditions that must hold at the same time.',
            'The solution is where the graphs cross — the point on every line at once.',
            'Substitution and elimination find that point without drawing it.',
            'Parallel lines give no solution; identical lines give infinitely many.',
            'A solution is a pair, and it must satisfy every equation.',
          ]),
        ],
      },
    },
  }),
];
